import type { StreamEffectStyle } from './stream-api';

export type WelcomeEffectsSnapshot = {
  selectedStyle: StreamEffectStyle;
  welcomeChatDefaults: {
    returning: string;
    newViewer: string;
  };
  welcomeFallbacks: Array<{
    style: StreamEffectStyle;
    soundId: string | null;
    soundName: string | null;
    configured: boolean;
    enabled: boolean;
  }>;
  activeSession: {
    id: string;
    style: StreamEffectStyle;
    welcomeGeneration: number;
    showStarted: boolean;
    welcomeReleaseDueAt: string | null;
    welcomes: {
      queued: number;
      playing: number;
      welcomed: number;
      failed: number;
      skipped: number;
      total: number;
    };
  } | null;
};

export type WelcomeProfile = {
  userLogin: string;
  userId: string | null;
  displayName: string;
  soundId: string | null;
  soundName: string | null;
  enabled: boolean;
  styles: StreamEffectStyle[];
  displayOverrides: Partial<Record<StreamEffectStyle, string>>;
  chatText: string | null;
  chatOverrides: Partial<Record<StreamEffectStyle, string>>;
  createdAt: string;
  updatedAt: string;
};

export type WelcomeProfilesSnapshot = {
  profiles: WelcomeProfile[];
  sounds: Array<{
    id: string;
    name: string;
    enabled: boolean;
    soundboardVisible: boolean;
  }>;
  styles: StreamEffectStyle[];
};

type RequestError = { message?: string; error?: string };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as RequestError;
    throw new Error(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`);
  }
  return (await response.json()) as T;
}

export async function loadWelcomes(): Promise<{
  effects: WelcomeEffectsSnapshot;
  profiles: WelcomeProfilesSnapshot;
}> {
  const [effects, profiles] = await Promise.all([
    request<WelcomeEffectsSnapshot>('/api/v1/effects'),
    request<WelcomeProfilesSnapshot>('/api/v1/effects/profiles'),
  ]);
  return { effects, profiles };
}

export function saveWelcomeFallback(
  style: StreamEffectStyle,
  soundId: string | null,
): Promise<WelcomeEffectsSnapshot> {
  return request('/api/v1/effects/welcome-fallback', {
    method: 'PUT',
    body: JSON.stringify({ style, soundId }),
  });
}

export function saveWelcomeDefaults(returning: string, newViewer: string): Promise<WelcomeEffectsSnapshot> {
  return request('/api/v1/effects/welcome-chat-defaults', {
    method: 'PUT',
    body: JSON.stringify({ returning, newViewer }),
  });
}

export function saveWelcomeProfile(
  login: string,
  profile: {
    soundId: string | null;
    enabled: boolean;
    styles: StreamEffectStyle[];
    displayOverrides: Partial<Record<StreamEffectStyle, string>>;
    chatText: string | null;
    chatOverrides: Partial<Record<StreamEffectStyle, string>>;
  },
): Promise<WelcomeProfilesSnapshot> {
  return request(`/api/v1/effects/profiles/${encodeURIComponent(login)}`, {
    method: 'PUT',
    body: JSON.stringify(profile),
  });
}

export function deleteWelcomeProfile(login: string): Promise<WelcomeProfilesSnapshot> {
  return request(`/api/v1/effects/profiles/${encodeURIComponent(login)}`, { method: 'DELETE' });
}
